/**
 * SpecialPrompts — modal pickers driven by GameState.pendingPrompt.
 *
 * Each branch handles one PendingPrompt kind:
 *   - quick-learner            — pick attacker's discard, then mode
 *   - spartacus-opening        — Activate / Decline → +2 Stamina / Draw 2
 *   - zeus-wrathbrand          — multi-disarm picker over opp weapon slots
 *   - single-disarm            — radio picker over candidate slots
 *   - thrusting-release        — pick 0/1 weapon for +power
 *   - fuel-inferno             — pick how many Focus to spend
 *   - stance-swap              — modal asking whether to swap stance
 *   - pick-weapon              - Pick a weapon to add its power or block to something
 * sparta-defend lives in CombatDialogs.tsx (legacy compat).
 */
import { useState } from "react";
import type { Card } from "@/types/card";
import type { GameState, PendingPrompt } from "@/types/game";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { BoardCard } from "./BoardCard";
import { devNull } from "os";
import { slotConfig } from "@/game/rules/keywords";

interface SpecialPromptsProps {
  game: GameState;
  cardMap: Map<string, Card>;
  /** Replace pendingPrompt with a new value (null to dismiss). */
  onUpdatePrompt: (next: PendingPrompt | null) => void;
  /** Called when the user confirms the prompt. The component supplies the kind-specific result. */
  onConfirmPrompt: (result: PromptResult) => void;
}

export type PromptResult =
  | { kind: "quick-learner"; pickedCardDbId: string }
  | { kind: "spartacus-opening"; choice: "activate" | "decline" | "stamina" | "draw" }
  | { kind: "zeus-wrathbrand"; selectedSlots: number[] }
  | { kind: "single-disarm"; slotIdx: number | null }
  | { kind: "thrusting-release"; slotIdx: number | null }
  | { kind: "fuel-inferno"; focusToSpend: number }
  | { kind: "bloodforged-activate"; countersToSpend: number }
  | { kind: "stance-swap"; swap: boolean }
  | { kind: "pick-loadout-weapons"; selectedSlots: number[] }
  | { kind: "pick-weapon"; slotIdx: number | null }
  | { kind: "pick-damaging-attacks"; selectedSlotIds: string[] }
  // Phase 3
  | { kind: "search-deck-by-filter"; selectedInstanceIds: string[] }
  | { kind: "pick-hand-discard"; selectedInstanceIds: string[] }
  | { kind: "pick-discard-for-bonus"; selectedInstanceIds: string[] }
  | { kind: "vengeance-attack"; accepted: boolean };

export function SpecialPrompts({
  game,
  cardMap,
  onUpdatePrompt,
  onConfirmPrompt,
}: SpecialPromptsProps) {
  const p = game.pendingPrompt;
  if (!p) return null;

  switch (p.kind) {
    case "quick-learner":
      return (
        <QuickLearnerPicker
          prompt={p}
          cardMap={cardMap}
          onUpdate={onUpdatePrompt}
          onConfirm={(picked) =>
            onConfirmPrompt({ kind: "quick-learner", pickedCardDbId: picked })
          }
        />
      );
    case "spartacus-opening":
      return (
        <SpartacusOpening
          step={p.step}
          onCancel={() => onUpdatePrompt(null)}
          onChoose={(c) => onConfirmPrompt({ kind: "spartacus-opening", choice: c })}
        />
      );
    case "zeus-wrathbrand":
      return (
        <ZeusWrathbrand
          prompt={p}
          opponent={game.players[p.oppNum]}
          cardMap={cardMap}
          onCancel={() => onUpdatePrompt(null)}
          onConfirm={(sel) => onConfirmPrompt({ kind: "zeus-wrathbrand", selectedSlots: sel })}
        />
      );
    case "single-disarm":
      return (
        <SingleDisarmPicker
          prompt={p}
          opponent={game.players[p.oppNum]}
          cardMap={cardMap}
          onCancel={() => onUpdatePrompt(null)}
          onConfirm={(slotIdx) => onConfirmPrompt({ kind: "single-disarm", slotIdx })}
        />
      );
    case "thrusting-release":
      return (
        <ThrustingReleasePicker
          prompt={p}
          cardMap={cardMap}
          onConfirm={(slotIdx) => onConfirmPrompt({ kind: "thrusting-release", slotIdx })}
        />
      );
    case "pick-weapon":
      return (
        <EquipWeaponPicker
          prompt={p}
          cardMap={cardMap}
          onConfirm={(slotIdx) => onConfirmPrompt({ kind: "pick-weapon", slotIdx })}
        />
      );
    case "pick-damaging-attacks":
      return (
        <PickDamagingAttacks
          prompt={p}
          onCancel={() => onUpdatePrompt(null)}
          onConfirm={(slotIds) => onConfirmPrompt({ kind: "pick-damaging-attacks", selectedSlotIds: slotIds })}
        />
      )
    case "fuel-inferno":
      return (
        <FuelInfernoPicker
          prompt={p}
          onCancel={() => onUpdatePrompt(null)}
          onConfirm={(n) => onConfirmPrompt({ kind: "fuel-inferno", focusToSpend: n })}
        />
      );
    case "bloodforged-activate":
      return (
        <BloodforgedPicker
          prompt={p}
          onCancel={() => onUpdatePrompt(null)}
          onConfirm={(n) => onConfirmPrompt({ kind: "bloodforged-activate", countersToSpend: n })}
        />
      );
    case "stance-swap":
      return (
        <StanceSwapPrompt
          onChoose={(swap) => onConfirmPrompt({ kind: "stance-swap", swap })}
        />
      );
    case "pick-loadout-weapons":
      return (
        <LoadoutWeaponPicker
          prompt={p}
          owner={game.players[p.ownerNum]}
          cardMap={cardMap}
          onCancel={() => onUpdatePrompt(null)}
          onConfirm={(sel) =>
            onConfirmPrompt({ kind: "pick-loadout-weapons", selectedSlots: sel })
          }
        />
      );
    // Phase 3 prompts
    case "search-deck-by-filter":
      return (
        <SearchDeckByFilterPicker
          prompt={p}
          cardMap={cardMap}
          onCancel={() => onUpdatePrompt(null)}
          onConfirm={(sel) =>
            onConfirmPrompt({ kind: "search-deck-by-filter", selectedInstanceIds: sel })
          }
        />
      );
    case "pick-hand-discard":
      return (
        <PickHandDiscardPicker
          prompt={p}
          player={game.players[p.playerNum]}
          cardMap={cardMap}
          onCancel={() => onUpdatePrompt(null)}
          onConfirm={(sel) =>
            onConfirmPrompt({ kind: "pick-hand-discard", selectedInstanceIds: sel })
          }
        />
      );
    case "pick-discard-for-bonus":
      return (
        <PickDiscardForBonusPicker
          prompt={p}
          player={game.players[p.playerNum]}
          cardMap={cardMap}
          onCancel={() => onUpdatePrompt(null)}
          onConfirm={(sel) =>
            onConfirmPrompt({ kind: "pick-discard-for-bonus", selectedInstanceIds: sel })
          }
        />
      );
    case "vengeance-attack":
      return (
        <VengeanceAttackPrompt
          prompt={p}
          cardMap={cardMap}
          onDecline={() => onConfirmPrompt({ kind: "vengeance-attack", accepted: false })}
          onAccept={() => onConfirmPrompt({ kind: "vengeance-attack", accepted: true })}
        />
      );
    default:
      return null;
  }
}

// ─── Quick Learner ─────────────────────────────────────────────────────────

function QuickLearnerPicker({
  prompt,
  cardMap,
  onUpdate,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "quick-learner" }>;
  cardMap: Map<string, Card>;
  onUpdate: (p: PendingPrompt | null) => void;
  onConfirm: (cardDbId: string) => void;
}) {
  const cards = prompt.legalDiscard
    .map((id) => cardMap.get(id))
    .filter((c): c is Card => Boolean(c));

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onUpdate(null)}
      title="Quick Learner — choose a card"
      className="max-w-2xl"
    >
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          Select a card from your opponent's discard. The picked card replaces
          Quick Learner in your sig slot and is playable any time during the
          round.
        </p>
        <div className="flex flex-wrap gap-2">
          {cards.map((c) => (
            <BoardCard
              key={c.id}
              card={c}
              size="hand"
              onClick={() => onConfirm(c.id)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ─── Spartacus Opening Advantage ───────────────────────────────────────────

function SpartacusOpening({
  step,
  onCancel,
  onChoose,
}: {
  step: "activate" | "choose";
  onCancel: () => void;
  onChoose: (c: "activate" | "decline" | "stamina" | "draw") => void;
}) {
  return (
    <Modal open onOpenChange={(o) => !o && onCancel()} title="Spartacus, Undefeated">
      {step === "activate" ? (
        <div className="space-y-3">
          <p className="text-sm text-[#94a3b8]">
            Spartacus' Opening Advantage is available (≥ 3 Focus). Activate now to spend 3 Focus and choose: +2 Stamina or Draw 2.
          </p>
          <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
            <Button variant="secondary" onClick={() => onChoose("decline")}>
              Decline
            </Button>
            <Button onClick={() => onChoose("activate")}>Activate</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-[#94a3b8]">Choose your Opening Advantage:</p>
          <div className="flex justify-center gap-2">
            <Button onClick={() => onChoose("stamina")}>Gain 2 Stamina</Button>
            <Button onClick={() => onChoose("draw")}>Draw 2 Cards</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Zeus' Wrathbrand multi-disarm ─────────────────────────────────────────

function ZeusWrathbrand({
  prompt,
  opponent,
  cardMap,
  onCancel,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "zeus-wrathbrand" }>;
  opponent: GameState["players"][1];
  cardMap: Map<string, Card>;
  onCancel: () => void;
  onConfirm: (selected: number[]) => void;
}) {
  const [selected, setSelected] = useState<number[]>(prompt.selectedSlots);

  const weaponSlots: { idx: number; card: Card }[] = [];
  opponent.equipSlots.forEach((s, idx) => {
    if (!s.cardId) return;
    const c = cardMap.get(s.cardId);
    if (!c) return;
    if ((c.equipmentFields?.equipmentSubtype ?? "").toLowerCase() === "weapon") {
      weaponSlots.push({ idx, card: c });
    }
  });

  function toggle(idx: number) {
    setSelected((s) => (s.includes(idx) ? s.filter((x) => x !== idx) : [...s, idx]));
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onCancel()}
      title="Zeus' Wrathbrand — Disarm Target(s)"
      className="max-w-2xl"
    >
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">Select weapons to disarm.</p>
        <div className="flex flex-wrap gap-2">
          {weaponSlots.map(({ idx, card }) => (
            <BoardCard
              key={idx}
              card={card}
              size="slot"
              slotLabel={`SLOT ${idx + 1}`}
              selected={selected.includes(idx)}
              onClick={() => toggle(idx)}
            />
          ))}
          {weaponSlots.length === 0 && (
            <p className="text-xs text-[#64748b]">Opponent has no weapons.</p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={() => onConfirm([])}>
            Skip
          </Button>
          <Button onClick={() => onConfirm(selected)} disabled={selected.length === 0}>
            Confirm ({selected.length} selected)
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Single-target disarm ──────────────────────────────────────────────────

function SingleDisarmPicker({
  prompt,
  opponent,
  cardMap,
  onCancel,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "single-disarm" }>;
  opponent: GameState["players"][1];
  cardMap: Map<string, Card>;
  onCancel: () => void;
  onConfirm: (slotIdx: number | null) => void;
}) {
  const [picked, setPicked] = useState<number | null>(null);

  const candidates = prompt.candidateSlots
    .map((idx) => ({ idx, card: opponent.equipSlots[idx]?.cardId ? cardMap.get(opponent.equipSlots[idx].cardId!) : null }))
    .filter((x): x is { idx: number; card: Card } => Boolean(x.card));

  if (candidates.length === 0) {
    return (
      <Modal open onOpenChange={(o) => !o && onCancel()} title="Disarm — no targets">
        <div className="space-y-3">
          <p className="text-sm text-[#94a3b8]">Opponent has no valid targets.</p>
          <div className="flex justify-end pt-3">
            <Button onClick={() => onConfirm(null)}>Dismiss</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onOpenChange={(o) => !o && onCancel()} title="Disarm — pick target">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {candidates.map(({ idx, card }) => (
            <BoardCard
              key={idx}
              card={card}
              size="slot"
              slotLabel={`SLOT ${idx + 1}`}
              selected={picked === idx}
              onClick={() => setPicked(idx)}
            />
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={() => onConfirm(null)}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(picked)} disabled={picked === null}>
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Thrusting Release ─────────────────────────────────────────────────────

function ThrustingReleasePicker({
  prompt,
  cardMap,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "thrusting-release" }>;
  cardMap: Map<string, Card>;
  onConfirm: (slotIdx: number | null) => void;
}) {
  return (
    <Modal
      open
      onOpenChange={() => {}}
      title="Thrusting Release — pick a weapon"
      className="max-w-2xl"
    >
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          Pick one of your ready weapons to add bonus power. Or decline.
        </p>
        <div className="flex flex-wrap gap-2">
          {prompt.readySlots.map((s) => {
            const card = cardMap.get(s.cardId);
            const bonus = Math.max(0, s.power - 2);
            return (
              <BoardCard
                key={s.slotIdx}
                card={card ?? null}
                size="slot"
                slotLabel={`+${bonus} POWER`}
                onClick={() => onConfirm(s.slotIdx)}
              />
            );
          })}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={() => onConfirm(null)}>
            Decline
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Equip Weapon Picker ──────────────────────────────────────────────────

function EquipWeaponPicker({
  prompt,
  cardMap,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "pick-weapon" }>;
  cardMap: Map<string, Card>;
  onConfirm: (slotIdx: number | null) => void;
}) {
  return (
    <Modal
      open
      onOpenChange={() => {}}
      title="Pick a Weapon"
      className="max-w-2xl"
    >
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          Pick one of your eligible weapons.
        </p>
        <div className="flex flex-wrap gap-2">
          {prompt.eligibleSlots.map((s) => {
            const card = cardMap.get(s.cardId);
            return (
              <BoardCard
                key={s.slotIdx}
                card={card ?? null}
                size="slot"
                slotLabel={s.displayLabel}
                onClick={() => onConfirm(s.slotIdx)}
              />
            );
          })}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={() => onConfirm(null)}>
            Decline
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Pick Damaging Attacks picker

function PickDamagingAttacks({
  prompt,
  onCancel,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "pick-damaging-attacks" }>;
  onCancel: () => void;
  /** Slot ids are strings (CombatSlot.id) — NOT numbers. */
  onConfirm: (selectedSlotIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>(prompt.selectedSlotIds);

  function toggle(slotId: string) {
    setSelected((s) =>
      s.includes(slotId) ? s.filter((x) => x !== slotId) : [...s, slotId],
    );
  }

  const exactlyN = prompt.maxSelectable;
  return (
    <Modal open onOpenChange={() => onCancel()} title={`${prompt.sourceCardName}: pick attacks`}>
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          Select {exactlyN} of your attacks that dealt damage this round. Their
          combined damage will be added to your next attack's power.
        </p>
        <div className="flex flex-wrap gap-2">
          {prompt.attacks.map((a) => (
            <Button
              key={a.slotId}
              variant={selected.includes(a.slotId) ? "primary" : "ghost"}
              onClick={() => toggle(a.slotId)}
              disabled={!selected.includes(a.slotId) && selected.length >= exactlyN}
            >
              {a.displayLabel}
            </Button>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={() => onConfirm([])}>
            Skip
          </Button>
          <Button
            onClick={() => onConfirm(selected)}
            disabled={selected.length !== exactlyN}
          >
            Confirm ({selected.length}/{exactlyN})
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Fuel The Inferno ──────────────────────────────────────────────────────

function FuelInfernoPicker({
  prompt,
  onCancel,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "fuel-inferno" }>;
  onCancel: () => void;
  onConfirm: (n: number) => void;
}) {
  const [n, setN] = useState<number>(prompt.selected);
  const max = prompt.maxFocus;

  return (
    <Modal open onOpenChange={(o) => !o && onCancel()} title="Fuel The Inferno">
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          Sacrifice Focus tokens. For each Focus chosen, you gain 1 stamina.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setN((v) => Math.max(1, v - 1))}
            disabled={n <= 1}
          >
            −
          </Button>
          <span className="rounded bg-amber-500/20 px-4 py-2 font-mono text-2xl text-amber-soft">
            {n}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setN((v) => Math.min(max, v + 1))}
            disabled={n >= max}
          >
            +
          </Button>
          <span className="text-xs text-[#64748b]">/ {max} available</span>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(n)}>Confirm</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Bloodforged Plate (Activate) ──────────────────────────────────────────

function BloodforgedPicker({
  prompt,
  onCancel,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "bloodforged-activate" }>;
  onCancel: () => void;
  onConfirm: (n: number) => void;
}) {
  const [n, setN] = useState<number>(prompt.selected);
  const max = prompt.maxCounters;

  return (
    <Modal open onOpenChange={(o) => !o && onCancel()} title="Activate Bloodforged">
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          Spend Blood counters. For each counter chosen, your next attack gains +1 power.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setN((v) => Math.max(1, v - 1))}
            disabled={n <= 1}
          >
            −
          </Button>
          <span className="rounded bg-red-500/20 px-4 py-2 font-mono text-2xl text-red-300">
            {n}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setN((v) => Math.min(max, v + 1))}
            disabled={n >= max}
          >
            +
          </Button>
          <span className="text-xs text-[#64748b]">/ {max} available</span>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(n)}>Confirm</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Stance Swap ───────────────────────────────────────────────────────────

function StanceSwapPrompt({ onChoose }: { onChoose: (swap: boolean) => void }) {
  return (
    <Modal open onOpenChange={() => {}} title="Swap Stance?">
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          A new stance is in your hand. Would you like to swap it in for your current stance?
        </p>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={() => onChoose(false)}>
            Skip
          </Button>
          <Button onClick={() => onChoose(true)}>Swap</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Loadout Weapon Picker (exhaust / ready N weapons) ────────────────────

function LoadoutWeaponPicker({
  prompt,
  owner,
  cardMap,
  onCancel,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "pick-loadout-weapons" }>;
  owner: GameState["players"][1];
  cardMap: Map<string, Card>;
  onCancel: () => void;
  onConfirm: (selected: number[]) => void;
}) {
  const [selected, setSelected] = useState<number[]>(prompt.selectedSlots);

  const candidates: { idx: number; card: Card | null }[] = prompt.candidateSlots.map((idx) => ({
    idx,
    card: owner.equipSlots[idx]?.cardId ? cardMap.get(owner.equipSlots[idx].cardId!) ?? null : null,
  }));

  function toggle(idx: number) {
    setSelected((s) => {
      if (s.includes(idx)) return s.filter((x) => x !== idx);
      if (s.length >= prompt.count) return s; // cap at requested count
      return [...s, idx];
    });
  }

  const verb = prompt.effect === "exhaust" ? "Exhaust" : "Ready";
  const noun = prompt.count === 1 ? "weapon" : "weapons";
  const title = `${prompt.cardName} — ${verb} ${prompt.count} ${noun}`;

  if (candidates.length === 0) {
    return (
      <Modal open onOpenChange={(o) => !o && onCancel()} title={`${verb} — no targets`}>
        <div className="space-y-3">
          <p className="text-sm text-[#94a3b8]">No eligible weapons.</p>
          <div className="flex justify-end pt-3">
            <Button onClick={() => onConfirm([])}>Dismiss</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onOpenChange={(o) => !o && onCancel()} title={title} className="max-w-2xl">
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          Select {prompt.count} {noun} to {prompt.effect}. ({selected.length} / {prompt.count}{" "}
          chosen)
        </p>
        <div className="flex flex-wrap gap-2">
          {candidates.map(({ idx, card }) => (
            <BoardCard
              key={idx}
              card={card}
              size="slot"
              slotLabel={`SLOT ${idx + 1}`}
              selected={selected.includes(idx)}
              onClick={() => toggle(idx)}
            />
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={() => onConfirm([])}>
            Skip
          </Button>
          <Button
            onClick={() => onConfirm(selected)}
            disabled={selected.length !== prompt.count}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Phase 3 Pickers ────────────────────────────────────────────────────────

/**
 * SearchDeckByFilterPicker — shows candidate deck cards, single/multi select,
 * confirm button. Title comes from prompt.filterLabel.
 * Used by Chain the Flow (and future tutor effects).
 */
function SearchDeckByFilterPicker({
  prompt,
  cardMap,
  onCancel,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "search-deck-by-filter" }>;
  cardMap: Map<string, Card>;
  onCancel: () => void;
  onConfirm: (selectedInstanceIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>(prompt.selectedInstanceIds);

  const candidates = prompt.candidates
    .map((c) => ({ instanceId: c.instanceId, card: cardMap.get(c.cardId) }))
    .filter((c): c is { instanceId: string; card: Card } => Boolean(c.card));

  function toggle(instanceId: string) {
    setSelected((s) => {
      if (s.includes(instanceId)) return s.filter((x) => x !== instanceId);
      if (s.length >= prompt.pickCount) {
        // Single-select: replace; multi-select: cap and ignore if at max.
        return prompt.pickCount === 1 ? [instanceId] : s;
      }
      return [...s, instanceId];
    });
  }

  const count = prompt.pickCount;
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onCancel()}
      title={prompt.filterLabel}
      className="max-w-3xl"
    >
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          {candidates.length === 0
            ? "No matching cards found in your deck."
            : `Select ${count} card${count !== 1 ? "s" : ""} to add to hand.`}
          {prompt.shuffleAfter ? " Deck will be shuffled after." : ""}
        </p>
        {candidates.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {candidates.map(({ instanceId, card }) => (
              <BoardCard
                key={instanceId}
                card={card}
                size="hand"
                selected={selected.includes(instanceId)}
                onClick={() => toggle(instanceId)}
              />
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(selected)}
            disabled={candidates.length > 0 && selected.length !== count}
          >
            {candidates.length === 0 ? "Dismiss" : `Add to hand (${selected.length}/${count})`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * PickHandDiscardPicker — shows the player's hand; player selects N cards to
 * discard. Used by Calculate Risk ("draw 2, discard 1").
 */
function PickHandDiscardPicker({
  prompt,
  player,
  cardMap,
  onCancel,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "pick-hand-discard" }>;
  player: GameState["players"][1];
  cardMap: Map<string, Card>;
  onCancel: () => void;
  onConfirm: (selectedInstanceIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>(prompt.selectedInstanceIds);

  const handCards = player.handInstances
    .map((h) => ({ instanceId: h.id, card: cardMap.get(h.cardId) }))
    .filter((h): h is { instanceId: string; card: Card } => Boolean(h.card));

  function toggle(instanceId: string) {
    setSelected((s) =>
      s.includes(instanceId)
        ? s.filter((x) => x !== instanceId)
        : s.length >= prompt.discardCount
        ? s
        : [...s, instanceId],
    );
  }

  const count = prompt.discardCount;
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onCancel()}
      title={`${prompt.sourceCardName} — Discard ${count} card${count !== 1 ? "s" : ""}`}
      className="max-w-3xl"
    >
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          Choose {count} card{count !== 1 ? "s" : ""} from your hand to discard.
        </p>
        <div className="flex flex-wrap gap-2">
          {handCards.map(({ instanceId, card }) => (
            <BoardCard
              key={instanceId}
              card={card}
              size="hand"
              selected={selected.includes(instanceId)}
              onClick={() => toggle(instanceId)}
            />
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(selected)}
            disabled={selected.length !== count}
          >
            Discard ({selected.length}/{count})
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * PickDiscardForBonusPicker — discard cards from hand for a power/block bonus.
 * Shows a running bonus preview ("Discard 3 → +3 power").
 * Used by The Final Straw and any future "discard-for-bonus" card.
 */
function PickDiscardForBonusPicker({
  prompt,
  player,
  cardMap,
  onCancel,
  onConfirm,
}: {
  prompt: Extract<PendingPrompt, { kind: "pick-discard-for-bonus" }>;
  player: GameState["players"][1];
  cardMap: Map<string, Card>;
  onCancel: () => void;
  onConfirm: (selectedInstanceIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>(prompt.selectedInstanceIds);

  const handCards = player.handInstances
    .map((h) => ({ instanceId: h.id, card: cardMap.get(h.cardId) }))
    .filter((h): h is { instanceId: string; card: Card } => Boolean(h.card));

  function toggle(instanceId: string) {
    setSelected((s) =>
      s.includes(instanceId)
        ? s.filter((x) => x !== instanceId)
        : [...s, instanceId],
    );
  }

  const bonusPerCard = prompt.bonusPerCard ?? 1;
  const totalBonus = selected.length * bonusPerCard;
  const modeName = prompt.mode === "attack" ? "power" : "block";

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onCancel()}
      title={`${prompt.sourceCardName} — Discard for Bonus`}
      className="max-w-3xl"
    >
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          Discard any number of cards for +{bonusPerCard} {modeName} each.
          {selected.length > 0 && (
            <span className="ml-2 font-semibold text-amber-soft">
              Discard {selected.length} → +{totalBonus} {modeName}
            </span>
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {handCards.map(({ instanceId, card }) => (
            <BoardCard
              key={instanceId}
              card={card}
              size="hand"
              selected={selected.includes(instanceId)}
              onClick={() => toggle(instanceId)}
            />
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={() => onConfirm([])}>
            Skip
          </Button>
          <Button onClick={() => onConfirm(selected)}>
            Confirm{selected.length > 0 ? ` (+${totalBonus} ${modeName})` : " (no bonus)"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * VengeanceAttackPrompt — displayed when the player takes ≥N damage and has
 * Vengeance Strike in hand. Asks: "Pay X stamina to play Vengeance Strike
 * as your attack?"
 */
function VengeanceAttackPrompt({
  prompt,
  cardMap,
  onDecline,
  onAccept,
}: {
  prompt: Extract<PendingPrompt, { kind: "vengeance-attack" }>;
  cardMap: Map<string, Card>;
  onDecline: () => void;
  onAccept: () => void;
}) {
  const card = cardMap.get(prompt.cardId);

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onDecline()}
      title={`${prompt.sourceCardName} — Vengeance!`}
    >
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          You took <span className="font-bold text-red-400">{prompt.damageReceived} damage</span>{" "}
          instead of fully blocking. Pay{" "}
          <span className="font-bold text-amber-soft">{prompt.staminaCost} stamina</span> to play{" "}
          <span className="font-semibold text-white">{prompt.sourceCardName}</span> as your attack
          this round?
        </p>
        {card && (
          <div className="flex justify-center">
            <BoardCard card={card} size="hand" />
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onDecline}>
            Decline
          </Button>
          <Button onClick={onAccept}>
            Strike! (−{prompt.staminaCost} stamina)
          </Button>
        </div>
      </div>
    </Modal>
  );
}
